// 證據包驗證（plan/vms-web-v0.md §5.8 EXP-04）：拖入 ZIP → 瀏覽器端解開、逐檔 SHA-256、用平台公開金鑰驗 manifest.sig。
// 檔案不上傳；只向平台拿公開金鑰（各 plane 的 /v1/vms/exports/keys，目前與歷史每一把；換 key 後舊包照樣驗得過，platform#60）。
// 結果寫是哪一把 key（代號、指紋、使用中 / 已停用）；頁尾可展開平台的驗章金鑰與完整指紋，給收件人跟公開文件比對。
// 兩個入口（SPEC #vms-web-540、#vms-web-220 轉址表；VMS UX 評審 2026-10-13 B-20）：
//   /verify：給外部收件人，免登入、沒有殼層；/exports/verify：登入的人在殼層內「匯出與證據 › 驗證證據包」分頁（embedded）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { planeBase } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { fingerprintText, uniqueKeys, verdict, verifyPackage, type PublicKey, type VerifyResult } from '../../lib/evidence';
import { stamp } from '../../lib/timefmt';

export function VerifyPage({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const { config } = useApp();
  const [res, setRes] = useState<VerifyResult | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [known, setKnown] = useState<PublicKey[] | null>(null);

  async function keys(): Promise<PublicKey[]> {
    const out: PublicKey[] = [];
    for (const api of Object.values(config.planes ?? {})) {
      try {
        const r = await fetch(`${planeBase(api)}/v1/vms/exports/keys`);
        if (r.ok) out.push(...(((await r.json()) as { keys: PublicKey[] }).keys ?? []));
      } catch {
        /* 某個 plane 連不上：其他的照用 */
      }
    }
    const u = uniqueKeys(out);
    setKnown(u);
    return u;
  }

  async function check(f: File) {
    setBusy(true);
    setName(f.name);
    setRes(null);
    try {
      const data = new Uint8Array(await f.arrayBuffer());
      setRes(await verifyPackage(data, await keys()));
    } finally {
      setBusy(false);
    }
  }

  const v = res ? verdict(res) : null;
  const Wrap = embedded ? 'section' : 'main';
  return (
    <Wrap className={embedded ? 'verify-embedded' : 'share-page'} data-testid="verify-page">
      {!embedded && (
        <header className="share-head">
          <strong>{config.display_name}</strong>
          <span className="muted">{t('verify.title')}</span>
        </header>
      )}
      <p className="muted small">{t('verify.help')}</p>
      <label
        className={`verify-drop ${over ? 'over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          const f = e.dataTransfer.files[0];
          if (f) void check(f);
        }}
      >
        <input type="file" accept=".zip,application/zip" hidden onChange={(e) => e.target.files?.[0] && void check(e.target.files[0])} />
        {busy ? t('verify.checking') : t('verify.drop')}
      </label>
      {res && (
        <section className="card" data-verdict={v}>
          <h2 className={`verify-${v}`}>{t(`verify.v_${v}`)}</h2>
          <p className="muted small">{name}</p>
          {res.error && <p className="error">{t(`verify.err_${res.error}`)}</p>}
          {res.manifest && (
            <dl className="verify-meta small">
              <dt>{t('verify.job')}</dt>
              <dd className="mono">{res.manifest.job_id}</dd>
              <dt>{t('verify.exported_by')}</dt>
              <dd className="mono">{res.manifest.exported_by}</dd>
              <dt>{t('verify.reason')}</dt>
              <dd>{res.manifest.reason}</dd>
              <dt>{t('verify.generated')}</dt>
              <dd>{stamp(res.manifest.generated_at)}</dd>
              <dt>{t('verify.signature')}</dt>
              <dd>{t(`verify.sig_${res.signature}`)}</dd>
              {res.key && (
                <>
                  <dt>{t('verify.key')}</dt>
                  <dd data-testid="verify-key">
                    <span className="mono">{res.key.key_id}</span>
                    {res.key.fingerprint && <span className="mono muted"> · {t('verify.fp_short', { fp: fingerprintText(res.key.fingerprint, true) })}</span>}
                    {res.key.status && <span className="badge"> {t(`verify.key_${res.key.status}`)}</span>}
                  </dd>
                </>
              )}
            </dl>
          )}
          <ul className="ai-list">
            {res.files.map((f) => (
              <li key={f.name}>
                <span>{f.ok ? '✅' : '❌'}</span>
                <span className="mono small">{f.name}</span>
                {!f.ok && <span className="error small">{f.missing ? t('verify.missing') : t('verify.mismatch')}</span>}
              </li>
            ))}
            {res.extra.map((n) => (
              <li key={n}>
                <span>⚠️</span>
                <span className="mono small">{n}</span>
                <span className="error small">{t('verify.extra')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {known && known.length > 0 && (
        <details className="small" data-testid="verify-keys">
          <summary>{t('verify.keys_title', { n: known.length })}</summary>
          <p className="muted">{t('verify.keys_help')}</p>
          <ul className="plain-list">
            {known.map((k) => (
              <li key={k.fingerprint ?? k.key_id}>
                <span className="mono">{k.key_id}</span>
                {k.status && <span className="badge"> {t(`verify.key_${k.status}`)}</span>}
                {k.fingerprint && <div className="mono muted">{fingerprintText(k.fingerprint)}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Wrap>
  );
}
